/* Out of Stock Report — API integration.
 * Base: /pharmacy-ims/api/v1/reports  (httpSupplier already targets
 * REACT_APP_SUPPLIER_API_BASE_URL = …/pharmacy-ims/api/, so paths are v1/reports/…).
 *
 * Every JSON response uses the envelope:
 *   { success, message, data, timestamp }
 * `unwrap` returns `data`, and throws `message` when success === false so the
 * screen can surface it as an error toast.
 *
 * NOTE: endpoint paths below are the documented contract for this feature
 * (list + async job export). If the backend names differ, change them here —
 * this is the single integration point. */
import moment from "moment";
import { httpSupplier } from "../../HttpCommonALL";
import { getCurrentUserName } from "../../../Services/warehouseContext";
import { saveAs } from "file-saver";

/* Audit header — resolved per call to the signed-in user. */
const xUser = () => ({ "X-User": getCurrentUserName() });

const DATE_FMT = "YYYY-MM-DD";

/* Default filter window: the last 7 days up to now. Returned as the separate
   date/time pieces the DateTimeRangeFilter binds to. Shared by every screen
   that lists out-of-stock orders (the report + the PO picker) so the default
   and the ISO formatting stay in one place. */
export const last7DaysRange = () => {
  const to = moment();
  const from = moment().subtract(7, "days");
  return {
    fromDate: from.format(DATE_FMT),
    fromTime: from.format("HH:mm"),
    toDate: to.format(DATE_FMT),
    toTime: to.format("HH:mm"),
  };
};

/* date + time pieces -> the ISO datetimes the API expects
   (YYYY-MM-DDTHH:mm:ss). A missing time defaults to the start (from) or end
   (to) of the day; a missing date drops the bound entirely (it is optional). */
export const toDateTimeRange = (f = {}) => ({
  from: f.fromDate ? `${f.fromDate}T${f.fromTime || "00:00"}:00` : "",
  to: f.toDate ? `${f.toDate}T${f.toTime || "23:59"}:59` : "",
});

const BASE = "v1/reports/out-of-stock";
/* Async export tasks are tracked under the generic reports-tasks endpoint
   (shared across report types), not under out-of-stock/export. */
const TASKS = "v1/reports/tasks";

const unwrap = (r) => {
  const env = r?.data;
  if (env && typeof env === "object" && "success" in env) {
    if (env.success === false) throw new Error(env.message || "Request failed");
    return env.data;
  }
  return env;
};

/* Build the shared query object from the screen's filter state.
   `status` is omitted for "All"; blank values are dropped. `from`/`to` are ISO
   datetimes (YYYY-MM-DDTHH:mm:ss) and `search` are all optional. `sort` controls
   ordering by created time and defaults to ASC (oldest first). */
const toQuery = (filters = {}, extra = {}) => {
  const q = {};
  if (filters.warehouseId) q.warehouseId = filters.warehouseId.trim();
  if (filters.status && filters.status !== "ALL") q.status = filters.status;
  if (filters.from) q.from = filters.from;
  if (filters.to) q.to = filters.to;
  if (filters.search) q.search = filters.search.trim();
  q.sort = filters.sort || "ASC";
  return { ...q, ...extra };
};

/* A stuck order's line item (backend key: `lines[]`). */
const fromItem = (it = {}) => ({
  itemId: it.lineId ?? it.itemId ?? null,
  drugCode: it.drugCode ?? it.medicineCode ?? "",
  drugName: it.drugName ?? it.medicineName ?? "",
  quantity: Number(it.orderedQuantity ?? it.quantity ?? 0) || 0,
  availableQty: Number(it.availableQuantity ?? 0) || 0,
  shortageQty: Number(it.shortageQuantity ?? 0) || 0,
  unitPrice: Number(it.unitPrice ?? 0) || 0,
  status: (it.status ?? "").toString().toUpperCase(),
});

/* Map a backend stuck-order row to the UI shape. */
const fromRow = (r = {}) => ({
  id: r.id ?? r.orderId,
  orderId: r.orderId ?? r.order_id ?? r.id ?? "",
  patientId: r.patientId ?? r.patient_id ?? "",
  warehouseId: r.hospitalId ?? r.warehouseId ?? r.warehouse_id ?? "",
  sourceChannel: r.sourceChannel ?? "",
  orderType: r.orderType ?? null,
  totalMrp: Number(r.totalMrp ?? 0) || 0,
  paidAmount: Number(r.paidAmount ?? 0) || 0,
  stuckReason: r.stuckReason ?? "",
  shortageQty: Number(r.totalShortageQuantity ?? r.shortageQty ?? 0) || 0,
  status: (r.status ?? "").toString().toUpperCase(),
  createdAt: r.createdAt ?? r.created_at ?? r.createdDate ?? r.orderCreateDate ?? "",
  items: Array.isArray(r.lines)
    ? r.lines.map(fromItem)
    : Array.isArray(r.items)
    ? r.items.map(fromItem)
    : [],
});

/* Normalize an async-export task envelope payload:
   { taskId, reportType, status, fileName, rowCount, errorMessage, ... }
   -> { taskId, status: PENDING | READY | FAILED, raw, fileName, rowCount, message }. */
const normalizeTask = (data = {}) => {
  const raw = (data.status ?? data.state ?? "").toString().toUpperCase();
  let status = "PENDING";
  if (["READY", "COMPLETED", "COMPLETE", "SUCCESS", "DONE"].includes(raw)) status = "READY";
  else if (["FAILED", "FAILURE", "ERROR"].includes(raw)) status = "FAILED";
  return {
    taskId: data.taskId ?? data.id ?? data.reportId ?? null,
    status,
    raw,
    fileName: data.fileName ?? data.filename ?? null,
    rowCount: data.rowCount ?? null,
    message: data.errorMessage ?? data.message ?? null,
  };
};

const outOfStockService = {
  /* GET v1/reports/out-of-stock -> paged list of stuck orders. */
  list(filters = {}, { page = 0, size = 20 } = {}) {
    return httpSupplier
      .get(BASE, { params: toQuery(filters, { page, size }), headers: xUser() })
      .then((r) => {
        const data = unwrap(r);
        const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
        return {
          content: content.map(fromRow),
          totalPages: data?.totalPages ?? (content.length ? 1 : 0),
          totalElements: data?.totalElements ?? content.length,
          page: data?.number ?? page,
          size: data?.size ?? size,
        };
      });
  },

  /* POST v1/reports/out-of-stock/export -> start CSV generation.
     data: { taskId, reportType, status, fileName, rowCount, errorMessage, ... }.
     Returns the normalized task so the caller can poll by taskId and name the
     download with the server-provided fileName. */
  requestExport(filters = {}) {
    return httpSupplier.post(`${BASE}/export`, toQuery(filters), { headers: xUser() }).then((r) => normalizeTask(unwrap(r)));
  },

  /* GET v1/reports/tasks/{taskId} -> the task object, polled until COMPLETED / FAILED. */
  exportStatus(taskId) {
    return httpSupplier.get(`${TASKS}/${taskId}`, { headers: xUser() }).then((r) => normalizeTask(unwrap(r)));
  },

  /* GET v1/reports/tasks/{taskId}/download -> CSV blob; saves to disk. */
  download(taskId, filename = "out-of-stock-report.csv") {
    return httpSupplier
      .get(`${TASKS}/${taskId}/download`, { responseType: "blob", headers: xUser() })
      .then((r) => {
        saveAs(new Blob([r.data], { type: "text/csv;charset=utf-8" }), filename);
      });
  },
};

export default outOfStockService;
